import { useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { Bug, Lightbulb, MessageSquare } from "lucide-react";
import { useLanguage } from "@/core/LanguageContext";
import { Button } from "@/shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { toast } from "@/shared/ui/use-toast";

type FeedbackType = "issue" | "feature_request";

interface FeedbackFormController {
  appendToDom: () => void;
  removeFromDom: () => void;
  open: () => void;
}

const feedbackEnabled = import.meta.env.VITE_SENTRY_FEEDBACK_ENABLED !== "false";
const feedbackButtonEnabled = import.meta.env.VITE_SENTRY_FEEDBACK_BUTTON_ENABLED !== "false";

export const SentryFeedbackButton = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [openingType, setOpeningType] = useState<FeedbackType | null>(null);
  const activeFormRef = useRef<FeedbackFormController | null>(null);
  const activeFormKeyRef = useRef<string | null>(null);

  if (!feedbackEnabled || !feedbackButtonEnabled || !Sentry.isEnabled()) {
    return null;
  }

  const openFeedbackForm = async (feedbackType: FeedbackType) => {
    const feedback = Sentry.getFeedback();

    if (!feedback) {
      toast({
        title: t("core.feedback.unavailable.title", "Feedback is unavailable"),
        description: t(
          "core.feedback.unavailable.description",
          "Please try again in a moment. The feedback widget is still loading.",
        ),
      });
      return;
    }

    setOpen(false);
    setOpeningType(feedbackType);

    const isIssue = feedbackType === "issue";
    const feedbackColorScheme = document.documentElement.classList.contains("dark") ? "dark" : "light";
    const formKey = `${feedbackType}:${feedbackColorScheme}`;

    try {
      const cachedForm = activeFormKeyRef.current === formKey ? activeFormRef.current : null;

      if (cachedForm) {
        cachedForm.open();
        return;
      }

      if (activeFormRef.current) {
        activeFormRef.current.removeFromDom();
        activeFormRef.current = null;
        activeFormKeyRef.current = null;
      }

      const createdForm = await feedback.createForm({
        tags: {
          feedback_type: feedbackType,
          route: window.location.pathname,
        },
        colorScheme: feedbackColorScheme,
        formTitle: isIssue
          ? t("core.feedback.issue.formTitle", "Report an issue")
          : t("core.feedback.feature.formTitle", "Request a feature"),
        submitButtonLabel: isIssue
          ? t("core.feedback.issue.submit", "Send issue report")
          : t("core.feedback.feature.submit", "Send feature request"),
        messagePlaceholder: isIssue
          ? t(
              "core.feedback.issue.placeholder",
              "What happened, what did you expect, and what steps should we try?",
            )
          : t(
              "core.feedback.feature.placeholder",
              "What would you like to improve and what problem would it solve?",
            ),
      });

      createdForm.appendToDom();
      activeFormRef.current = createdForm;
      activeFormKeyRef.current = formKey;
      createdForm.open();
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("core.feedback.openError.title", "Could not open feedback form"),
        description: t(
          "core.feedback.openError.description",
          "Please refresh the page and try again.",
        ),
      });
      console.error("Failed to open Sentry feedback form", error);
    } finally {
      setOpeningType(null);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-[1000]">
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button type="button" size="sm" className="gap-2 rounded-full px-4 shadow-industrial">
            <MessageSquare className="h-4 w-4" />
            {t("core.feedback.trigger", "Feedback")}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="top" className="w-72">
          <DropdownMenuLabel>{t("core.feedback.menu.title", "Send product feedback")}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="gap-2"
            disabled={openingType !== null}
            onSelect={() => {
              void openFeedbackForm("issue");
            }}
          >
            <Bug className="h-4 w-4" />
            {t("core.feedback.menu.issue", "Report an issue")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="gap-2"
            disabled={openingType !== null}
            onSelect={() => {
              void openFeedbackForm("feature_request");
            }}
          >
            <Lightbulb className="h-4 w-4" />
            {t("core.feedback.menu.feature", "Request a feature")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <p className="px-2 py-1 text-xs text-muted-foreground">
            {t(
              "core.feedback.menu.hint",
              "You can include a screenshot and your recent replay session with the report.",
            )}
          </p>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
